import { Box, Drawer, FormControlLabel, IconButton, useMediaQuery } from "@mui/material";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/ArrowUp.svg";
import CloseIcon from "assets/CloseIcon";
import { ReactComponent as FilterIconBlue } from "assets/FilterIcon.svg";
import { ReactComponent as Filter } from "assets/filter.svg";
import { ReactComponent as FilterIconBlueMobile } from "assets/filter_icon_mobile.svg";
import { fetchAdminInncodesAsync } from "components/features/MyPlans/myplans.slice";
import { useTranslate } from "hooks/useTranslate";
import { cloneDeep, uniqueId } from "lodash";
import * as React from "react";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import colors from "styles/Colors";
import { tokens } from "styles/Tokens";
import { corpFilterConstants, minSearchStrLength, optionTypeArray, sourceList } from "utils/CommonConstants";
import { getGlobalLevelFilterCount, useDebouncedEffect } from "utils/CommonFunctions";
import { theme as MUITheme } from "../../../styles/Theme";
import Button from "../Button";
import CheckBox from "../Checkbox";
import CollapsibleContainer from "../CollapsibleContainer";
import ButtonLink from "../Link";
import Typography from "../Typography";
import SearchAppBar from "../searchBar";
import { ResItem } from "./Filters";
import {
  fetchFilterPropertyInfo,
  fetchFilterSelectedPlansAsync,
  fetchManagementcOInfo,
  fetchOwnerInfo,
  searchOwners,
  setCheckedDynamicInncode,
  setCheckedOwnerInfo,
  setCheckedPlanInfo,
  setCurrentParent,
  setDefaultFilterModified,
  setFilters,
  setView
} from "./Filters.slice";

interface Props {
  filters: ResItem[];
  parentPage: string;
  defaultAppliedFilter?: any;
  view?: string;
  isDefaultButtonsApplicable?: boolean;
  updateSavedFilter?: any;
}

function Filters(props: Props) {
  const dispatch = useAppDispatch();
  const [openDrawer, setOpenDrawer] = useState(false);
  const {
    filters,
    parentPage = "",
    defaultAppliedFilter,
    view = "",
    isDefaultButtonsApplicable = false,
    updateSavedFilter
  } = props;
  const [expanded, setExpanded] = React.useState<string[]>([]);
  const [checkedList, setCheckedList] = React.useState<any>([]);
  const [innCodeSearchValue, setInnCodeSearchValue] = useState("");
  const translate = useTranslate();
  const [planSearchValue, setPlanSearchValue] = useState("");
  const [managementSearchValue, setManagementSearchValue] = useState("");
  const [ownerGroupSearchValue, setOwnerGroupSearchValue] = useState("");
  const [ownerSearchValue, setOwnerSearchValue] = useState("");
  const adminInncodes = useAppSelector((state) => state.myplans.MyPlans.adminInncodes);
  const plansList: any = useAppSelector((state) => state.filters.searchPlanList);
  const managementList: any = useAppSelector((state) => state.filters.searchManagements);
  const ownerGroupList: any = useAppSelector((state) => state.filters.searchOwnersGroup);
  const ownerList: any = useAppSelector((state) => state.filters.searchOwners);
  const selectedFilters: any = useAppSelector((state) => state.filters.filters);
  const modifiedFilters: any = useAppSelector((state) => state.filters.modifiedFilters);
  const doNotUpdateFilterState: any = useAppSelector((state) => state.filters.doNotUpdateFilterState);
  const propertyInfo: any = useAppSelector((state) => state.filters.propertyList);
  const planInfo: any = useAppSelector((state) => state.filters.planInfo);
  const ownerInfo: any = useAppSelector((state) => state.filters.ownerInfo);
  const defaultFilterModified: any = useAppSelector((state) => state.filters.defaultFilterModified)[parentPage];
  const isMobileScreen = useMediaQuery(MUITheme.breakpoints.down("md"));
  const usersList = useAppSelector((state) => state.aboveProperty.roleCategoryList);

  const DynamicViewByType: any = {
    value: {
      inncode: innCodeSearchValue,
      planCmsId: planSearchValue,
      managementCompany: managementSearchValue,
      ownerGroup: ownerGroupSearchValue,
      planOwner: ownerSearchValue
    },
    change: {
      inncode: setInnCodeSearchValue,
      planCmsId: setPlanSearchValue,
      managementCompany: setManagementSearchValue,
      ownerGroup: setOwnerGroupSearchValue,
      planOwner: setOwnerSearchValue
    },
    result: {
      inncode: adminInncodes,
      planCmsId: plansList,
      managementCompany: managementList,
      ownerGroup: ownerGroupList,
      planOwner: ownerList
    }
  };

  React.useEffect(() => {
    setCheckedList([...selectedFilters]);
  }, [filters, selectedFilters]);

  React.useEffect(() => {
    dispatch(setCurrentParent({ parentPage: parentPage }));
    if (modifiedFilters[parentPage]?.length && !selectedFilters?.length) {
      dispatch(setFilters(modifiedFilters[parentPage]));
      dispatch(setDefaultFilterModified({ isFilterChanged: true, isApplied: true }));
    } else if (defaultAppliedFilter?.filters?.length && !selectedFilters?.length)
      dispatch(setFilters(defaultAppliedFilter?.filters));
    if (view) dispatch(setView(view));
  }, [defaultAppliedFilter, modifiedFilters]);

  useDebouncedEffect(
    () => {
      if (innCodeSearchValue && innCodeSearchValue.length > minSearchStrLength)
        dispatch(fetchAdminInncodesAsync(innCodeSearchValue));
    },
    [innCodeSearchValue],
    500
  );
  useDebouncedEffect(
    () => {
      if (managementSearchValue && managementSearchValue.length > minSearchStrLength)
        dispatch(fetchManagementcOInfo(managementSearchValue));
    },
    [managementSearchValue],
    500
  );

  useDebouncedEffect(
    () => {
      if (ownerGroupSearchValue && ownerGroupSearchValue.length > minSearchStrLength)
        dispatch(fetchOwnerInfo(ownerGroupSearchValue));
    },
    [ownerGroupSearchValue],
    500
  );

  useDebouncedEffect(
    () => {
      if (planSearchValue && planSearchValue.length > minSearchStrLength) {
        dispatch(
          fetchFilterSelectedPlansAsync({
            payload: { searchText: planSearchValue, filter: { innCode: "CORP" } },
            isSearched: true
          })
        );
      }
    },
    [planSearchValue],
    500
  );

  useDebouncedEffect(
    () => {
      if (ownerSearchValue && ownerSearchValue.length > minSearchStrLength) {
        dispatch(searchOwners({ userList: usersList, searchText: ownerSearchValue }));
      }
    },
    [ownerSearchValue],
    500
  );

  const toggleInput = React.useCallback(
    (open: any) => (event: React.KeyboardEvent | React.MouseEvent | React.SyntheticEvent<Element, Event>) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setOpenDrawer(open);
    },
    [setOpenDrawer]
  );

  const handleChange = (panel: string, key: string) => {
    let newArr = [...expanded];
    const position = expanded.indexOf(panel);
    if (position > -1) newArr.splice(position, 1);
    setExpanded(position > -1 ? newArr : [...expanded, panel]);

    if (key === corpFilterConstants.innCode) checkAndGetInncodeDetails();
    else if (key === corpFilterConstants.planName) checkAndGetPlanDetails();
  };

  const checkAndGetInncodeDetails = () => {
    const options = checkedList.find((item: any) => item.key === corpFilterConstants.innCode)?.options;
    const filtered = options?.filter(
      (item: string) => propertyInfo?.findIndex((prop: any) => prop.innCode === item) < 0
    );
    if (filtered?.length) dispatch(fetchFilterPropertyInfo({ innCodesValue: filtered }));
  };

  const checkAndGetPlanDetails = () => {
    const options = checkedList.find((item: any) => item.key === corpFilterConstants.planName)?.options;
    const filtered = options?.filter((item: string) => planInfo?.findIndex((prop: any) => prop.id === item) < 0);
    if (filtered?.length)
      dispatch(
        fetchFilterSelectedPlansAsync({
          payload: {
            filter: {
              ids: filtered
            }
          }
        })
      );
  };

  const getChildernSelectedCount = (item: any) => {
    const options = checkedList.find((checkedList: any) => checkedList.key === item.key)?.options;
    let count: number = 0;
    if (item?.optionsType === "key-array-value-array" && options?.length) {
      sourceList?.map((item: any) => {
        if (options?.includes(item.value[0])) count++;
      });
    } else count = options?.length;
    if (count) return "(" + count + ")";
  };

  const getParentCheckBoxes = () => {
    return filters.map((item: any) => {
      if (item[parentPage] === "Y")
        return (
          <React.Fragment key={item.id}>
            <Box
              sx={{
                ...{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 25px",
                  color: tokens.Text.General.PrimaryHeadline
                },
                ...{}
              }}
            >
              <Box display={"flex"} flexDirection={"row"} gap={"8px"}>
                <Typography variant="body1" showBold>
                  {item.name}
                </Typography>
                <Typography variant="body1">{getChildernSelectedCount(item)}</Typography>
              </Box>

              <IconButton aria-label="expand" onClick={() => handleChange(item.id, item.key)}>
                {expanded.indexOf(item.id) > -1 ? <ArrowUp /> : <ArrowDown />}
              </IconButton>
            </Box>

            <CollapsibleContainer
              style={{ maxWidth: isMobileScreen ? "100%" : "350px" }}
              isOpen={expanded.indexOf(item.id) > -1}
            >
              {getChildrenCheckboxes(item)}
              {checkedList?.find((value: any) => value?.key === item.key)?.options?.length > 0 && (
                <Typography
                  variant="body2"
                  showBold
                  color={tokens.Text.General.Color}
                  sx={{ paddingX: "50px", paddingY: "20px", cursor: "pointer" }}
                  onClick={() => {
                    setCheckedList((prevList: any) => prevList.filter((value: any) => value.key !== item.key));
                    if (defaultAppliedFilter)
                      dispatch(
                        setDefaultFilterModified({ isFilterChanged: true, isApplied: defaultFilterModified?.isApplied })
                      );
                  }}
                >
                  Clear Filter
                </Typography>
              )}
            </CollapsibleContainer>
          </React.Fragment>
        );
    });
  };

  const getParentOptions = (isInncodeType: boolean, item: any, parent: any) => {
    let options;
    if (isInncodeType) {
      options = item?.innCode;
    } else if (parent.key === corpFilterConstants.management || parent.key === corpFilterConstants.ownerGroup) {
      options = item;
    } else {
      options = item?.id;
    }
    return options;
  };

  const getDynamicCategoryResult = (isInncodeType: boolean, inputValue: any, parent: any, results: any) => {
    if (inputValue && !results?.length && parent.key === corpFilterConstants.owners)
      return (
        <Typography
          sx={{ paddingX: "15px" }}
          variant={"label"}
          children={translate("common.memberNotFound")}
        ></Typography>
      );
    else
      return (
        inputValue &&
        results?.map((item: any) => {
          const isCheckedFunc = () => {
            if (checkedList.length === 0) {
              return false;
            }

            const parentCheckedItem = checkedList.find((checkedItem: any) => checkedItem.key === parent.key);

            if (parentCheckedItem) {
              const options = getParentOptions(isInncodeType, item, parent);
              const optionIndex = parentCheckedItem.options.indexOf(options);
              return optionIndex > -1;
            }

            return false;
          };
          const isChecked = isCheckedFunc();
          return (
            <Box
              key={uniqueId("OPTIONS_")}
              sx={{
                ...{
                  paddingLeft: "25px",
                  color: tokens.Text.General.PrimaryHeadline
                },
                ...{}
              }}
            >
              <FormControlLabel
                label={
                  <>
                    <Typography variant="body2">{item?.name || item}</Typography>
                    {isInncodeType && <Typography variant="body2">{item?.innCode}</Typography>}
                  </>
                }
                control={
                  <CheckBox
                    id={uniqueId("OPTIONS2_")}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChildrenChange(e, {
                        parentId: parent.id,
                        parentKey: parent.key,
                        option: getParentOptions(isInncodeType, item, parent),
                        name: item?.name,
                        parentName: parent.name
                      })
                    }
                    checked={isChecked}
                  />
                }
              />
            </Box>
          );
        })
      );
  };

  const getUserViewName = (isInncodeType: boolean, value: any, parentKey: string) => {
    if (isInncodeType) return propertyInfo?.find((item: any) => item?.innCode === value)?.propName;
    else if (parentKey === corpFilterConstants.management || parentKey === corpFilterConstants.ownerGroup) return value;
    else if (parentKey === corpFilterConstants.owners) return ownerInfo?.find((item: any) => item?.id === value)?.name;
    else return planInfo?.find((item: any) => item?.id === value)?.name;
  };

  const getSelectedDynamicCategoryResult = (isInncodeType: boolean, parentKey: string) => {
    return checkedList
      ?.find((item: any) => item?.key === parentKey)
      ?.options?.map((item: any) => {
        return (
          <Box
            key={item}
            sx={{
              ...{
                paddingLeft: "25px",
                color: tokens.Text.General.PrimaryHeadline
              },
              ...{}
            }}
          >
            <FormControlLabel
              label={
                <>
                  <Typography variant="body2">{getUserViewName(isInncodeType, item, parentKey)}</Typography>
                  {isInncodeType && <Typography variant="body2">{item}</Typography>}
                </>
              }
              control={
                <CheckBox
                  id={item}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChildrenChange(e, {
                      parentId: parentKey,
                      parentKey: parentKey,
                      option: item
                    })
                  }
                  checked={true}
                />
              }
            />
          </Box>
        );
      });
  };

  const getChildernValueToCheckIsChecked = (optionType: string, subFilter: any) => {
    if (optionTypeArray.includes(optionType)) return Array.isArray(subFilter?.key) ? subFilter?.key[0] : subFilter?.key;
    else return subFilter;
  };

  const getDynamicView = (parentKey: string, type: string) => {
    return DynamicViewByType[type][parentKey];
  };

  const getChildrenCheckboxes = (parent: any) => {
    return (
      <Box>
        {parent?.isDynamic === "Y" ? (
          <Box display={"flex"} flexDirection={"column"} gap={"10px"} paddingX={"17px"}>
            <Box padding={"10px"}>
              <SearchAppBar
                value={getDynamicView(parent?.key, "value")}
                getInputDetails={(e: any) => {
                  const fun: any = getDynamicView(parent?.key, "change");
                  fun(e);
                }}
                planType={"filter"}
                sx={{ border: "1px solid", borderColor: tokens.Border.General.Tertiary }}
              ></SearchAppBar>
            </Box>

            {!getDynamicView(parent?.key, "value") &&
              getSelectedDynamicCategoryResult(parent?.key === corpFilterConstants.innCode, parent?.key)}
            {getDynamicCategoryResult(
              parent?.key === corpFilterConstants.innCode,
              getDynamicView(parent?.key, "value"),
              parent,
              getDynamicView(parent?.key, "result")
            )}
          </Box>
        ) : (
          parent.filters?.map((subFilter: any) => {
            const isChecked = checkedList.length
              ? checkedList
                  .find((checkedList: any) => checkedList.key === parent.key)
                  ?.options.indexOf(getChildernValueToCheckIsChecked(parent?.optionsType, subFilter)) > -1
              : false;

            return (
              <Box
                key={subFilter}
                sx={{
                  ...{
                    paddingLeft: "50px",
                    color: tokens.Text.General.PrimaryHeadline
                  },
                  ...{}
                }}
              >
                <FormControlLabel
                  label={
                    <Typography variant="body2">
                      {optionTypeArray.includes(parent?.optionsType) ? subFilter?.value : subFilter}
                    </Typography>
                  }
                  control={
                    <CheckBox
                      id={subFilter?.id}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChildrenChange(e, {
                          parentId: parent.id,
                          parentKey: parent.key,
                          optionType: parent?.optionsType,
                          parentName: parent.name,
                          option: optionTypeArray.includes(parent?.optionsType) ? subFilter?.key : subFilter
                        })
                      }
                      checked={isChecked}
                    />
                  }
                />
              </Box>
            );
          })
        )}
      </Box>
    );
  };

  const handleChildrenChange = (event: React.ChangeEvent<HTMLInputElement>, data: any) => {
    const checked = event.target.checked;
    const newCheckedList = cloneDeep(checkedList);

    const foundItemIndex = newCheckedList.findIndex((item: any) => item.key === data?.parentKey);
    if (checked) {
      if (foundItemIndex !== -1) {
        newCheckedList[foundItemIndex].options =
          data?.optionType === "key-array-value-array"
            ? [...newCheckedList[foundItemIndex].options, ...(data?.option || [])]
            : [...newCheckedList[foundItemIndex].options, data?.option];
      } else {
        newCheckedList.push({
          id: data?.parentId,
          options: data?.optionType === "key-array-value-array" ? [...(data?.option || [])] : [data?.option],
          key: data?.parentKey,
          name: data?.parentName
        });
      }
    } else {
      if (newCheckedList[foundItemIndex].options?.length === 1) {
        newCheckedList.splice(foundItemIndex, 1);
      } else {
        newCheckedList[foundItemIndex].options = newCheckedList[foundItemIndex].options?.filter((item: any) =>
          data?.optionType === "key-array-value-array" ? !data?.option?.includes(item) : item !== data?.option
        );
      }
    }
    if (defaultAppliedFilter)
      dispatch(setDefaultFilterModified({ isFilterChanged: true, isApplied: defaultFilterModified?.isApplied }));

    setCheckedList(newCheckedList);
    if (data?.parentKey === corpFilterConstants.innCode && checked)
      dispatch(setCheckedDynamicInncode({ innCode: data?.option, propName: data?.name }));
    else if (data?.parentKey === corpFilterConstants.planName && checked)
      dispatch(setCheckedPlanInfo({ id: data?.option, name: data?.name }));
    else if (data?.parentKey === corpFilterConstants.owners && checked)
      dispatch(setCheckedOwnerInfo({ id: data?.option, name: data?.name }));
  };

  const getOptions = () => (
    <Box
      key={"options"}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%"
      }}
    >
      <Box minWidth={"350px"} maxWidth={"470px"} role="presentation">
        {
          <Box
            sx={{
              position: "sticky",
              top: 0,
              padding: "25px 25px 25px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "white",
              zIndex: 1
            }}
          >
            <Typography variant={"h5"} showBold color={tokens.Text.General.Secondary}>
              Filters
            </Typography>
            <Typography sx={{ marginLeft: "8px" }} variant={"h5"} color={tokens.Text.General.Secondary}>
              (
              {checkedList?.reduce(
                (total: any, obj: { options: string | any[] }) =>
                  total + (Array.isArray(obj.options) ? getGlobalLevelFilterCount(obj) : 0),
                0
              )}
              )
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", marginLeft: "auto" }}>
              {defaultFilterModified?.isFilterChanged && isDefaultButtonsApplicable && (
                <IconButton
                  sx={{ width: "60%" }}
                  onClick={() => {
                    setCheckedList(defaultAppliedFilter?.filters);
                    dispatch(
                      setDefaultFilterModified({ isApplied: defaultFilterModified?.isApplied, isFilterChanged: false })
                    );
                  }}
                >
                  <FilterIconBlue />
                </IconButton>
              )}

              <IconButton onClick={toggleInput(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        }
        <Box>{getParentCheckBoxes()}</Box>
      </Box>
      {getFooter()}
    </Box>
  );

  const getFooter = () => {
    return (
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-evenly",

          padding: "20px",
          background: "white"
        }}
      >
        <Button
          variant="primary"
          label="Apply"
          disabled={!checkedList?.length}
          sx={{ width: "100%" }}
          onClick={() => {
            dispatch(setFilters(checkedList));
            if (defaultAppliedFilter)
              dispatch(
                setDefaultFilterModified({ isFilterChanged: defaultFilterModified?.isFilterChanged, isApplied: true })
              );
            setOpenDrawer(false);
          }}
        />
      </Box>
    );
  };

  const getDrawerLayout = () => {
    return (
      <>
        <Box sx={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "center" }}>
          {selectedFilters?.length > 0 && !defaultAppliedFilter?.filters?.length && (
            <ButtonLink
              text="Clear all"
              sx={{ color: tokens.Text.General.Color, fontWeight: "700", textDecoration: "underline" }}
              textVariant="body2"
              onClick={() => dispatch(setFilters([]))}
            />
          )}
          {defaultFilterModified?.isFilterChanged && defaultFilterModified?.isApplied && isDefaultButtonsApplicable && (
            <>
              <ButtonLink
                text="Save as Default"
                sx={{ color: tokens.Text.General.Color, fontWeight: "700", textDecoration: "underline" }}
                textVariant="body2"
                onClick={updateSavedFilter}
              />
              {isMobileScreen ? (
                <IconButton
                  onClick={() => {
                    dispatch(setFilters(defaultAppliedFilter?.filters));
                    dispatch(setDefaultFilterModified({ isFilterChanged: false, isApplied: false }));
                  }}
                  style={{ border: "1px solid #004C97", width: "44px", height: "44px" }}
                  color="secondary"
                >
                  <FilterIconBlueMobile />
                </IconButton>
              ) : (
                <Button
                  onClick={() => {
                    dispatch(setFilters(defaultAppliedFilter?.filters));
                    dispatch(setDefaultFilterModified({ isFilterChanged: false, isApplied: false }));
                  }}
                  variant="secondary"
                  label={`Reset to Default`}
                />
              )}
            </>
          )}
          {isMobileScreen ? (
            <IconButton
              onClick={toggleInput("DRAWER")}
              style={{ backgroundColor: tokens.Icon.General.Color, width: "44px", height: "44px" }}
            >
              <Filter />
            </IconButton>
          ) : (
            <Button
              onClick={toggleInput("DRAWER")}
              startIcon={<Filter />}
              variant="primary"
              label={`Filters`}
              endIcon={
                selectedFilters?.length > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        backgroundColor: colors.Blue.Blue200,
                        paddingX: "8px",
                        borderRadius: "200px"
                      }}
                      variant={"body2"}
                      showBold
                    >
                      {selectedFilters?.reduce(
                        (total: any, obj: { options: string | any[] }) =>
                          total + (Array.isArray(obj.options) ? getGlobalLevelFilterCount(obj) : 0),
                        0
                      )}
                    </Typography>
                  </Box>
                )
              }
            />
          )}
        </Box>
        <Drawer
          anchor={"right"}
          open={openDrawer}
          onClose={toggleInput(false)}
          PaperProps={{
            sx: {
              justifyContent: "space-between",
              width: { xs: "100%", md: "auto" }
            }
          }}
        >
          {getOptions()}
        </Drawer>
      </>
    );
  };

  return getDrawerLayout();
}
export default React.memo(Filters);
